import React, { useContext, useState, useEffect } from "react";
import { Box, VStack, HStack, Text, Button, Icon, useBreakpointValue, Spinner } from "@chakra-ui/react";
import {
    MdDashboard,
    MdStorage,
    MdSettings,
    MdKey,
    MdFlag,
    MdImportExport,
    MdAdUnits,
} from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import FeatureUnlockContext from "../context/FeatureUnlockContext";
import ThemeContext from "../context/ThemeContext";
import Loading from "./Loading";
import Navbar from "./Navbar";
import UploadYourFile from "../pages/UploadYourFile";
import axios from "axios";

const navItems = [
    { name: "Dashboard", icon: MdDashboard, path: "/dashboard" },
    { name: "Datasets", icon: MdStorage, path: "/datasets" },
    { name: "Settings", icon: MdSettings, path: "/settings" },
    { name: "API Key", icon: MdKey, path: "/apiKey" },
    { name: "Personalization", icon: MdFlag, path: "/personalization" },
    { name: "Reports/Exports", icon: MdImportExport, path: '/reportsExports' },
    { name: "Audit", icon: MdAdUnits, path: '/auditCompliance'}
];

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

function SideBar() {
    const { token, user, setUser, logout } = useContext(AuthContext);
    const { resetUnlocks } = useContext(FeatureUnlockContext);
    const { themeData } = useContext(ThemeContext);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#3182CE';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';
    const textColor = themeData?.colors?.text || '#1A202C';

    // Determine if theme is dark
    const isDarkTheme = themeData?.id === 'midnight';

    // Sidebar background - use secondary for dark, background for light
    const sidebarBg = isDarkTheme ? secondaryColor : backgroundColor;
    
    // Active button uses primary color with proper text contrast
    const activeButtonBg = primaryColor;
    const activeButtonColor = isDarkTheme ? textColor : 'white';
    
    // Hover state - slightly lighter for dark themes, accent tint for light
    const hoverBg = isDarkTheme ? primaryColor : accentColor + '20';
    
    // Inactive text should use theme text color for proper contrast
    const inactiveTextColor = textColor;

    useEffect(() => {
        const fetchUser = async () => {
            if (!token || user) {
                setLoading(false);
                return;
            }

            try {
                const res = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                console.log("checking user: ", res);

                if (res.status === 420) {
                    console.error('Invalid token');
                    resetUnlocks();
                    logout();
                    return;
                }

                setUser(res.data);
            } catch (error) {
                console.error('Error fetching user:', error);
                if (error.response?.status === 420) {
                    console.error('Token expired or invalid, logging out...');
                    resetUnlocks();
                    logout();
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [token, user, setUser, logout, resetUnlocks]);

    if (!token || (!user && loading)) {
        return (
            <Loading />
        );
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center" bg={backgroundColor}>
                <Spinner size="xl" color={primaryColor} />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <UploadYourFile setUser={setUser} token={token} />
            </Box>
        )
    }

    if (isMobile) {
        const filteredNavItems = navItems.filter(item => {
            if (item.name === "Settings" && user?.role === "rm") return false;
            return true;
        });
        // Mobile navbar layout
        return (
            <Box
                position="fixed"
                bottom={0}
                left={0}
                right={0}
                bg={sidebarBg}
                p={2}
                borderTop="1px solid"
                borderColor={isDarkTheme ? primaryColor : 'gray.200'}
                zIndex={1000}
            >
                <HStack justify="space-around" spacing={1}>
                    {filteredNavItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Button
                                as={Link}
                                to={item.path}
                                key={item.name}
                                variant="ghost"
                                bg={isActive ? activeButtonBg : "transparent"}
                                color={isActive ? activeButtonColor : inactiveTextColor}
                                _hover={{
                                    bg: isActive ? activeButtonBg : hoverBg,
                                    color: isActive ? activeButtonColor : inactiveTextColor,
                                }}
                                borderRadius="md"
                                fontWeight={isActive ? "medium" : "normal"}
                                size="sm"
                                flex={1}
                                maxW="70px"
                                height="60px"
                                display="flex"
                                flexDirection="column"
                                gap={1}
                            >
                                <Icon as={item.icon} boxSize={5} />
                                <Text fontSize="xs" fontWeight="normal">
                                    {item.name}
                                </Text>
                            </Button>
                        );
                    })}
                </HStack>
            </Box>
        );
    }

    const filteredNavItems = navItems.filter(item => {
        if (item.name === "Settings" && user?.role === "rm") return false;
        return true;
    });
    
    // Desktop sidebar layout (original)
    return (
        <Box
            w="220px"
            h="100vh"
            bg={sidebarBg}
            p={4}
            display="flex"
            flexDirection="column"
            boxShadow="xl"
            position="sticky"
            top={0}
        >
            <VStack align="stretch" spacing={2}>
                {filteredNavItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <Button
                            as={Link}
                            to={item.path}
                            key={item.name}
                            justifyContent="flex-start"
                            leftIcon={<Icon as={item.icon} boxSize={5} />}
                            variant="ghost"
                            bg={isActive ? activeButtonBg : "transparent"}
                            color={isActive ? activeButtonColor : inactiveTextColor}
                            _hover={{
                                bg: isActive ? activeButtonBg : hoverBg,
                                color: isActive ? activeButtonColor : inactiveTextColor,
                            }}
                            borderRadius="md"
                            fontWeight={isActive ? "medium" : "normal"}
                            h="40px"
                            fontSize="15px"
                        >
                            <Text>{item.name}</Text>
                        </Button>
                    );
                })}
            </VStack>
        </Box>
    );
}

export default SideBar;